import React, { useState, useEffect, useMemo } from 'react';

// --- Icon Components (using inline SVG for single-file setup) ---
// In a real project, you would install and import these from 'lucide-react'
const Icon = ({ name, className }) => {
    const icons = {
        hospital: <path d="M12 6v4H8v4h4v4h4v-4h4v-4h-4V6h-4z" />,
        'flask-conical': <><path d="M10 2v7.38A5 5 0 0 0 5 17c0 1.66 1.34 3 3 3h8c1.66 0 3-1.34 3-3a5 5 0 0 0-5-7.62V2h-4z" /><path d="M8 20c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-1H8v1z" /></>,
        'file-check-2': <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" />,
        'file-pen-line': <path d="M12 22h6a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v5" />,
        'search-code': <><path d="m9 9-2 2 2 2" /><path d="m13 13 2-2-2-2" /><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></>,
        lock: <><rect width="18" height="11" x="3" y="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>,
        globe: <><circle cx="12" cy="12" r="10" /><path d="M2 12h20" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></>,
        eye: <><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" /></>,
        'arrow-up-right-from-circle': <><path d="M22 12A10 10 0 1 1 12 2" /><path d="M22 2 12 12" /><path d="M15 2h7v7" /></>,
        target: <><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></>,
        check: <path d="M20 6 9 17l-5-5" />,
        'check-circle': <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>,
    };
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
            {icons[name] || <circle cx="12" cy="12" r="10" />}
        </svg>
    );
};


// --- Style Component ---
const GlobalStyles = () => (
    <style>{`
        body {
            font-family: 'Inter', sans-serif;
        }
        .gradient-text {
            background: linear-gradient(to right, #1565C0, #42A5F5);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        .hp-cta {
            transition: all 0.3s ease;
        }
        .hp-cta:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
        }
        .feature-card, .testimonial-card, .factory-card {
             transition: all 0.3s ease;
             border: 1px solid transparent;
        }
        .feature-card:hover, .testimonial-card:hover, .factory-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
            border-color: #e2e8f0;
        }
        @keyframes logo-scroll {
            0% { transform: translateX(0); }
            100% { transform: translateX(-100%); }
        }
        .animate-logo-scroll {
            animation: logo-scroll 30s linear infinite;
        }
    `}</style>
);

// --- Components ---


const Hero = () => (
    <section id="hero" className="pb-20 md:pb-32 bg-neutral-50 pt-16 md:pt-24">
        <div id="hero-container" className="container mx-auto px-6 text-center">
            <h1 className="text-4xl md:text-6xl font-extrabold text-neutral-900 leading-tight">
                Go faster. <span className="gradient-text">Think deeper.</span> Do better.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-neutral-500 max-w-3xl mx-auto">
                A High Precision Framework for Healthcare & Life Sciences
            </p>
            <div id="hero-cta-buttons" className="mt-10 flex justify-center items-center gap-4">
                <a href="#demo" className="bg-primary text-white font-semibold px-8 py-3 rounded-lg shadow-lg hover:bg-primary-dark hp-cta">Get demo</a>
                <a href="#guide" className="bg-white text-primary border border-primary/20 font-semibold px-8 py-3 rounded-lg hover:bg-primary/5 hp-cta">Learn more</a>
            </div>
        </div>
    </section>
);

const LogoCarousel = () => {
    const logos = [
        { src: "https://placehold.co/120x40/0D47A1/FFFFFF?text=PharmaCo", alt: "PharmaCo Logo" },
        { src: "https://placehold.co/120x40/0D47A1/FFFFFF?text=MedTech", alt: "MedTech Logo" },
        { src: "https://placehold.co/120x40/0D47A1/FFFFFF?text=BioGen", alt: "BioGen Logo" },
        { src: "https://placehold.co/120x40/0D47A1/FFFFFF?text=Clinica", alt: "Clinica Logo" },
        { src: "https://placehold.co/120x40/0D47A1/FFFFFF?text=HealthSys", alt: "HealthSys Logo" },
        { src: "https://placehold.co/120x40/0D47A1/FFFFFF?text=CRO-Next", alt: "CRO-Next Logo" },
    ];
    const duplicatedLogos = [...logos, ...logos];

    return (
        <section id="logos" className="py-12 bg-white">
            <div id="logos-container" className="container mx-auto px-6">
                <h3 className="text-center text-neutral-500 font-medium">Trusted by leading companies</h3>
                <div id="logos-scroll-container" className="relative mt-8 h-12 overflow-hidden">
                    <div id="logos-scroll-track" className="absolute inset-0 w-full flex animate-logo-scroll hover:[animation-play-state:paused]">
                        <div id="logos-scroll-content" className="flex-shrink-0 w-max flex items-center justify-around gap-16">
                            {duplicatedLogos.map((logo, index) => (
                                <img key={index} src={logo.src} alt={logo.alt} className="h-8 object-contain" />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const Stats = () => (
    <section id="stats" className="py-20 bg-neutral-50">
        <div id="stats-container" className="container mx-auto px-6">
            <div id="stats-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 text-center">
                <div id="stat-experts" className="p-6">
                    <p className="text-5xl font-extrabold text-primary">200+</p>
                    <h4 className="mt-2 text-lg font-semibold text-neutral-900">Clinical Experts</h4>
                    <p className="mt-1 text-neutral-500">Clinical experts using DeepDocs AI</p>
                </div>
                <div id="stat-use-cases" className="p-6">
                    <p className="text-5xl font-extrabold text-primary">30+</p>
                    <h4 className="mt-2 text-lg font-semibold text-neutral-900">Use Cases</h4>
                    <p className="mt-1 text-neutral-500">Documented use cases across industries</p>
                </div>
                <div id="stat-time-saved" className="p-6">
                    <p className="text-5xl font-extrabold text-primary">&gt;90%</p>
                    <h4 className="mt-2 text-lg font-semibold text-neutral-900">Time Saved</h4>
                    <p className="mt-1 text-neutral-500">Average time savings in clinical workflows</p>
                </div>
                <div id="stat-documents" className="p-6">
                    <p className="text-5xl font-extrabold text-primary">10,000+</p>
                    <h4 className="mt-2 text-lg font-semibold text-neutral-900">Documents Processed</h4>
                    <p className="mt-1 text-neutral-500">Medical devices & drugs documents</p>
                </div>
            </div>
        </div>
    </section>
);

const AIFactories = () => (
    <section id="factories" className="py-20">
        <div id="factories-container" className="container mx-auto px-6">
            <div id="factories-header" className="text-center max-w-3xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-extrabold text-neutral-900">Purpose-built AI Factories for Expert Teams</h2>
                <p className="mt-4 text-lg text-neutral-500">Stop using generic AI tools. Get specialized workflows designed for regulatory compliance.</p>
            </div>
            <div id="factories-grid" className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
                {/* Cards would be mapped from an array in a real app */}
                <FactoryCard
                    icon="hospital"
                    color="blue"
                    title="Clinical Affairs Factory"
                    description="Literature analysis, evidence synthesis, and clinical evaluation"
                    features={[
                        { name: "Dam Select", text: "Automated study selection" },
                        { name: "Dam Assess", text: "Quality assessment" },
                        { name: "Dam Extract", text: "Data extraction" },
                        { name: "DeepWrite SR", text: "Report generation" },
                    ]}
                    insight="From literature search to regulatory-grade report"
                />
                <FactoryCard
                    icon="flask-conical"
                    color="green"
                    title="Clinical Investigation Factory"
                    description="Clinical study reports, protocol analysis, and data extraction"
                    features={[
                        { name: "Dam Extract", text: "Multi-format data extraction" },
                        { name: "DeepTemplate", text: "Protocol compliance check" },
                    ]}
                    insight="Streamline clinical data processing workflows"
                />
                <FactoryCard
                    icon="file-check-2"
                    color="purple"
                    title="Regulatory Affairs Factory"
                    description="Document comparison, template filling, and compliance reports"
                    features={[
                        { name: "DeepCompare", text: "Document version control" },
                        { name: "DeepTemplate", text: "Automated form filling" },
                        { name: "Dam Extract", text: "Regulatory data mining" },
                    ]}
                    insight="Ensure compliance across all submissions"
                />
                <FactoryCard
                    icon="file-pen-line"
                    color="orange"
                    title="Medical Writing Factory"
                    description="Scientific publication, medical communication, and document creation"
                    features={[
                        { name: "DeepWrite", text: "Scientific manuscript drafting" },
                        { name: "Dam Extract", text: "Reference compilation" },
                        { name: "DeepTemplate", text: "Journal submission formatting" },
                    ]}
                    insight="Accelerate publication-ready content creation"
                />
            </div>
        </div>
    </section>
);

const FactoryCard = ({ icon, color, title, description, features, insight }) => {
    const colorClasses = {
        blue: { bg: 'bg-blue-100', text: 'text-primary', check: 'text-accent', border: 'border-blue-300', insightBg: 'bg-blue-50', insightText: 'text-blue-800' },
        green: { bg: 'bg-green-100', text: 'text-green-700', check: 'text-green-500', border: 'border-green-300', insightBg: 'bg-green-50', insightText: 'text-green-800' },
        purple: { bg: 'bg-purple-100', text: 'text-purple-700', check: 'text-purple-500', border: 'border-purple-300', insightBg: 'bg-purple-50', insightText: 'text-purple-800' },
        orange: { bg: 'bg-orange-100', text: 'text-orange-700', check: 'text-orange-500', border: 'border-orange-300', insightBg: 'bg-orange-50', insightText: 'text-orange-800' },
    };
    const classes = colorClasses[color] || colorClasses.blue;

    return (
        <div id={`factory-card-${color}`} className="bg-white rounded-xl p-8 shadow-sm factory-card">
            <div id={`factory-header-${color}`} className="flex items-center gap-4">
                <div id={`factory-icon-${color}`} className={`${classes.bg} ${classes.text} p-3 rounded-full`}><Icon name={icon} /></div>
                <h3 className="text-2xl font-bold text-neutral-900">{title}</h3>
            </div>
            <p className="mt-4 text-neutral-500">{description}</p>
            <ul id={`factory-features-${color}`} className="mt-6 space-y-3 text-neutral-700">
                {features.map((feat, i) => (
                    <li key={i} className="flex items-start gap-3">
                        <Icon name="check-circle" className={`w-5 h-5 ${classes.check} flex-shrink-0 mt-1`} />
                        <span><strong className="font-semibold">{feat.name}</strong> - {feat.text}</span>
                    </li>
                ))}
            </ul>
            <div id={`factory-insight-${color}`} className={`mt-6 ${classes.insightBg} border-l-4 ${classes.border} p-4 rounded-r-lg`}>
                <p className={classes.insightText}><span className="font-bold">💡 {insight}</span></p>
            </div>
        </div>
    );
};

const Features = () => (
    <section id="features" className="py-20 bg-neutral-50">
        <div id="features-container" className="container mx-auto px-6">
            <div id="features-header" className="text-center max-w-3xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-extrabold text-neutral-900">Built for clinical excellence</h2>
            </div>
            <div id="features-grid" className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                <FeatureCard icon="search-code" title="Document Intelligence" text="Purpose-built for medical literature, clinical evaluation reports, and regulatory submissions with complete audit trails." />
                <FeatureCard icon="lock" title="Privacy by Design" text="Your clinical data never leaves your infrastructure. Deploy on-premise, in private clouds, or air-gapped environments." />
                <FeatureCard icon="globe" title="Global Compliance" text="Process regulatory documents in 25+ languages with understanding of cultural nuances and local regulations." />
                <FeatureCard icon="eye" title="Observable Workflows" text="Complete transparency in every processing step. Real-time monitoring and full traceability for compliance requirements." />
                <FeatureCard icon="arrow-up-right-from-circle" title="High Value Tasks" text="Extract complex data from documents - tables, charts, handwritten notes, and multi-column layouts." />
                <FeatureCard icon="target" title="High Performance Metrics" text="Revolutionary approach ensures factual outputs through source verification, cross-referencing, and confidence scoring." />
            </div>
        </div>
    </section>
);

const FeatureCard = ({ icon, title, text }) => (
    <div id={`feature-card-${icon}`} className="p-8 bg-white rounded-xl shadow-sm feature-card">
        <div id={`feature-icon-${icon}`} className="bg-blue-100 text-primary w-12 h-12 flex items-center justify-center rounded-full">
            <Icon name={icon} />
        </div>
        <h3 className="mt-4 text-xl font-bold text-neutral-900">{title}</h3>
        <p className="mt-2 text-neutral-500">{text}</p>
    </div>
);

const Clients = () => (
    <section id="clients" className="py-20">
        <div id="clients-container" className="container mx-auto px-6">
            <div id="clients-header" className="text-center max-w-3xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-extrabold text-neutral-900">Our partners talk highly of us</h2>
            </div>
            <div id="clients-grid" className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {/* Testimonials */}
                <TestimonialCard emoji="🇨🇭" company="Swiss Medical Device Company" quote="It's impressive the precision of what you can do and how easily you can automate with Deepdocs AI." author="Clinical trials manager of a Swiss medical device manufacturer and distributor" />
                <TestimonialCard emoji="🇺🇸" company="American Medical Devices Corporation" quote="The team have spoken highly of your product, and we are very pleased to be able to use DeepDocs AI." author="CEO of an American medical devices corporation" />
                <TestimonialCard emoji="🇫🇷" company="French CRO" quote="It's really powerful... DeepDocs AI assist our experts and save them precious time." author="Scientific director of a French CRO" />
                <TestimonialCard emoji="🇫🇷" company="French Bariatric Medical Devices Corporation" quote="Using Deepdocs has saved me at least 50% of my time, allowing me to refocus on other projects." author="Medical writer of a bariatric medical devices French corporation" />
                <TestimonialCard emoji="🇫🇷" company="French Medical Device Manufacturer" quote="Anything that saves time at the moment is very welcome, and that's just what we're going to tackle. Deeps Docs AI is great" author="CEO of a French medical device manufacturer" />
            </div>
        </div>
    </section>
);

const TestimonialCard = ({ emoji, company, quote, author }) => (
    <div id={`testimonial-${company.toLowerCase().replace(/\s+/g, '-')}`} className="bg-white rounded-xl p-8 shadow-sm testimonial-card">
        <div id={`testimonial-header-${company.toLowerCase().replace(/\s+/g, '-')}`} className="flex items-center gap-4">
            <span className="text-4xl">{emoji}</span>
            <div><h3 className="font-bold text-neutral-900">{company}</h3></div>
        </div>
        <blockquote className="mt-4 text-neutral-500 italic border-l-4 border-neutral-200 pl-4">{quote}</blockquote>
        <p className="mt-4 text-sm text-neutral-500">{author}</p>
    </div>
);

const SocialProof = () => (
    <section id="social-proof" className="py-20 bg-primary text-white">
        <div id="social-proof-container" className="container mx-auto px-6 text-center">
            <p className="text-sm font-bold uppercase tracking-widest text-accent">Latest deployment • Fortune 500 pharmaceutical • December 2024</p>
            <blockquote className="mt-6 text-2xl md:text-3xl font-medium max-w-4xl mx-auto">
                "The team have spoken highly of your product, and we are very pleased to be able to use DeepDocs AI for our clinical evaluation processes. The ROI was immediate."
            </blockquote>
            <p className="mt-4 font-semibold">CEO, Fortune 500 pharmaceutical company</p>
            <p className="mt-8 text-blue-200">3 more Fortune 500 companies currently in pilot phase</p>
        </div>
    </section>
);

const ROICalculator = () => {
    const [reports, setReports] = useState(20);
    const [hours, setHours] = useState(200);
    const [rate, setRate] = useState(150);

    const savings = useMemo(() => {
        const totalCost = reports * hours * rate;
        const savedAmount = totalCost * 0.90; // 90% savings
        return savedAmount.toLocaleString('en-US', { maximumFractionDigits: 0 });
    }, [reports, hours, rate]);

    return (
        <section id="roi-calculator" className="py-20">
            <div id="roi-container" className="container mx-auto px-6">
                <div id="roi-header" className="text-center max-w-3xl mx-auto">
                    <h2 className="text-3xl md:text-4xl font-extrabold text-neutral-900">Calculate Your ROI</h2>
                    <p className="mt-4 text-lg text-neutral-500">See how optimizing your clinical workflows with DeepDocs AI translates into tangible savings.</p>
                </div>
                <div id="roi-calculator-card" className="mt-12 max-w-4xl mx-auto bg-white p-8 rounded-2xl shadow-lg border border-neutral-200">
                    <div id="roi-calculator-grid" className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                        <div id="roi-inputs">
                            <div id="reports-input" className="mb-6">
                                <label htmlFor="reports" className="block text-sm font-medium text-neutral-700 mb-2">Number of clinical reports per year?</label>
                                <input type="number" id="reports" value={reports} onChange={(e) => setReports(Number(e.target.value))} className="w-full p-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary" />
                            </div>
                            <div id="hours-input" className="mb-6">
                                <label htmlFor="hours" className="block text-sm font-medium text-neutral-700 mb-2">Average hours per report?</label>
                                <input type="number" id="hours" value={hours} onChange={(e) => setHours(Number(e.target.value))} className="w-full p-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary" />
                            </div>
                            <div id="rate-input">
                                <label htmlFor="rate" className="block text-sm font-medium text-neutral-700 mb-2">Average hourly rate ($) for clinical staff?</label>
                                <input type="number" id="rate" value={rate} onChange={(e) => setRate(Number(e.target.value))} className="w-full p-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary" />
                            </div>
                        </div>
                        <div id="roi-results" className="text-center bg-neutral-50 p-8 rounded-xl">
                            <p className="text-neutral-500">Estimated Annual Savings</p>
                            <p id="savings" className="text-5xl font-extrabold text-primary my-2">${savings}</p>
                            <p className="text-neutral-500">with <span className="font-bold text-neutral-700">90%</span> time reduction</p>
                            <a href="#demo" className="mt-6 inline-block bg-primary text-white font-semibold px-6 py-3 rounded-lg shadow-sm hover:bg-primary-dark hp-cta">Get a Personalized ROI Report</a>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const CallToAction = () => (
    <section id="demo" className="py-20 bg-neutral-50">
        <div id="cta-container" className="container mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-extrabold text-neutral-900">Validated by the industry</h2>
            <p className="mt-4 text-lg text-neutral-500 max-w-3xl mx-auto">See how we're accelerating clinical operations for medical device, pharma & CRO teams worldwide</p>
            <a href="#demo-form" className="mt-10 inline-block bg-primary text-white font-bold text-lg px-10 py-4 rounded-lg shadow-xl hover:bg-primary-dark hp-cta">Book your demo slot this week</a>
            <div id="cta-features" className="mt-6 flex flex-wrap justify-center items-center gap-x-6 gap-y-2 text-neutral-500">
                <span className="flex items-center gap-2"><Icon name="check" className="w-4 h-4 text-green-500" />See your document type processed live</span>
                <span className="flex items-center gap-2"><Icon name="check" className="w-4 h-4 text-green-500" />Get ROI calculation</span>
                <span className="flex items-center gap-2"><Icon name="check" className="w-4 h-4 text-green-500" />Your data stays private</span>
            </div>
            <p className="mt-8 text-sm text-neutral-500">
                <strong className="text-neutral-700">Perfect for:</strong> Clinical affairs, regulatory affairs, medical writing teams
            </p>
        </div>
    </section>
);



// --- Main App Component ---
// This is the default export. In a real application, you would render this
// component to the DOM using ReactDOM.render().
export default function App() {
    return (
        <div id="app-root" className="bg-white text-neutral-700 font-sans antialiased">
            {/* In a real React project, you'd set up Tailwind CSS via a config file.
              The Google Font and custom styles are included here for simplicity.
            */}
            <GlobalStyles />
            
            <Hero />
            <LogoCarousel />
            <Stats />
            <AIFactories />
            <Features />
            <Clients />
            <SocialProof />
            <ROICalculator />
            <CallToAction />
        </div>
    );
}